import CaprockMark from '@/components/linear/caprock-mark';
import CommandPalette from '@/components/linear/command-palette';
import CreateIssueModal from '@/components/linear/create-issue-modal';
import PeekOverlay from '@/components/linear/peek-overlay';
import SettingsSidebar from '@/components/linear/settings-sidebar';
import ShortcutMenu from '@/components/linear/shortcut-menu';
import ShortcutsHelp from '@/components/linear/shortcuts-help';
import Sidebar from '@/components/linear/sidebar';
import WorkspaceChromeProvider, { useWorkspaceChrome } from '@/components/linear/workspace-chrome';
import Flash from '@/components/flash';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { applyWorkspaceTheme, readWorkspaceTheme, type WorkspaceTheme } from '@/lib/theme';
import { Head, router, usePage } from '@inertiajs/react';
import { Menu } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';

interface WorkspaceLayoutProps {
  children: React.ReactNode;
  flash: {
    notice?: string;
    error?: string;
  };
  pageTitle?: string;
}

interface WorkspaceShellProps {
  children: ReactNode;
  flash: WorkspaceLayoutProps['flash'];
  title: string;
  settingsMode: boolean;
  linearTheme?: Exclude<WorkspaceTheme, 'system'>;
}

const MAIN_CONTENT_ID = 'main-content';
const MOBILE_NAV_ID = 'workspace-mobile-nav';

export default function WorkspaceLayout({ children, flash, pageTitle }: WorkspaceLayoutProps) {
  const { props, url } = usePage<{ pageTitle?: string; currentPath?: string }>();
  const title = pageTitle || props.pageTitle || 'CAP Rock Labs';
  const path = props.currentPath || url.split('?')[0];
  const settingsMode = path.startsWith('/app/settings');

  const [theme, setThemeState] = useState<WorkspaceTheme>(() =>
    typeof window === 'undefined' ? 'dark' : readWorkspaceTheme()
  );

  useEffect(() => {
    applyWorkspaceTheme(theme);
    const onTheme = () => setThemeState(readWorkspaceTheme());
    window.addEventListener('caprock:theme', onTheme);
    return () => {
      window.removeEventListener('caprock:theme', onTheme);
      document.documentElement.classList.remove('dark');
      document.documentElement.removeAttribute('data-linear-theme');
    };
  }, [theme]);

  const linearTheme = theme === 'system' ? undefined : theme;

  return (
    <>
      <Head title={title} />
      <TooltipProvider>
        <WorkspaceChromeProvider>
          <WorkspaceShell
            flash={flash}
            title={title}
            settingsMode={settingsMode}
            linearTheme={linearTheme}
          >
            {children}
          </WorkspaceShell>
        </WorkspaceChromeProvider>
      </TooltipProvider>
    </>
  );
}

function WorkspaceShell({
  children,
  flash,
  title,
  settingsMode,
  linearTheme,
}: WorkspaceShellProps) {
  const { paletteOpen, createIssueOpen, shortcutsOpen } = useWorkspaceChrome();
  const overlayOpen = paletteOpen || createIssueOpen || shortcutsOpen;
  const [navOpen, setNavOpen] = useState(false);

  // Close the modal sheet in the same render as overlay open so Search / New
  // issue / shortcuts are not trapped under the Radix focus lock.
  if (navOpen && overlayOpen) {
    setNavOpen(false);
  }

  useEffect(() => {
    return router.on('navigate', () => setNavOpen(false));
  }, []);

  useEffect(() => {
    const media = window.matchMedia('(max-width: 800px)');
    const onChange = () => {
      if (!media.matches) setNavOpen(false);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return (
    <div data-linear data-linear-theme={linearTheme} className="ln-app">
      <a
        href={`#${MAIN_CONTENT_ID}`}
        className="ln-skip-link"
        onClick={() => {
          document.getElementById(MAIN_CONTENT_ID)?.focus();
        }}
      >
        Skip to main content
      </a>
      {settingsMode ? <SettingsSidebar /> : <Sidebar />}
      <div className="ln-main">
        <Sheet open={navOpen} onOpenChange={setNavOpen}>
          <header className="ln-mobile-topbar">
            <CaprockMark size={22} tone="on-dark" />
            <div className="ln-mobile-topbar-title">{title}</div>
            <SheetTrigger asChild>
              <button
                type="button"
                className="ln-icon-btn"
                aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
                aria-expanded={navOpen}
                aria-haspopup="dialog"
                aria-controls={MOBILE_NAV_ID}
              >
                <Menu size={18} />
              </button>
            </SheetTrigger>
          </header>
          <SheetContent
            side="left"
            id={MOBILE_NAV_ID}
            data-linear
            data-linear-theme={linearTheme}
            className="ln-mobile-drawer p-0"
            onCloseAutoFocus={(event) => {
              if (overlayOpen) event.preventDefault();
            }}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Workspace navigation</SheetTitle>
              <SheetDescription>Navigate the CAP Rock Labs workspace</SheetDescription>
            </SheetHeader>
            {navOpen ? settingsMode ? <SettingsSidebar /> : <Sidebar /> : null}
          </SheetContent>
        </Sheet>
        <main id={MAIN_CONTENT_ID} className="ln-main-content" tabIndex={-1}>
          <Flash {...flash} />
          <Toaster />
          {children}
        </main>
      </div>
      <CommandPalette />
      <CreateIssueModal />
      <PeekOverlay />
      <ShortcutMenu />
      <ShortcutsLayer />
    </div>
  );
}

function ShortcutsLayer() {
  const { shortcutsOpen, setShortcutsOpen } = useWorkspaceChrome();
  return <ShortcutsHelp open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />;
}
